/* eslint-disable no-debugger */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* tslint:disable: no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
/*import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';*/
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';

import { AttributesForSelectedVariantInput, getAttributesForSelectedVariant } from '@msdyn365-commerce-modules/product-specification';
/*import { ProductSpecificationTableRow } from './components/lobas-product-attribute-table-stucture';*/
import { ILobasProductAttributeData } from './lobas-product-attribute.data';
import { ILobasProductAttributeProps } from './lobas-product-attribute.props.autogenerated';

export interface ILobasProductAttributeViewProps extends ILobasProductAttributeProps<{}> {
    title?: React.ReactNode;
    ProductAttribute: IModuleProps;
    attribute?: (JSX.Element | null)[];
}

/**
 *
 * ProductSpecification component.
 * @extends {React.Component<ILobasProductAttributeProps<ILobasProductAttributeData>>}
 */
@observer
class LobasProductAttribute extends React.Component<ILobasProductAttributeProps<ILobasProductAttributeData>> {
    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;

        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        data.product.result.RecordId,

                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result
                    );

                    getAttributesForSelectedVariant(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public render(): JSX.Element | null {
        let data: ILobasProductAttributeData;

        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }

        const { className, selectedAttribute, heading } = this.props.config;
        /*const { productSpecificationData } = this.props.data;*/

        const viewProps = {
            ...this.props,
            title: heading?.text && (
                <Msdyn365.Text
                    className='lobas-product-attribute__heading'
                    tag={heading.tag ?? 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            ProductAttribute: {
                moduleProps: this.props,
                className: classnames('lobas-product-attribute', className)
            },
            attribute: this._createTextValue(data, selectedAttribute)
        } as ILobasProductAttributeViewProps;
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle Heading Text Change.
     * @param event - Current event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    public handleAttributeChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.selectedAttribute = event.target.value);

    private _createTextValue(content: ILobasProductAttributeData, selectedAttribute: string | undefined): (JSX.Element | null)[] {
        return content.productSpecificationData
            .result!.filter(name => name.Name === selectedAttribute)
            .map((product, index) => {
                const cellData: JSX.Element | null = this._renderProductCell(product);

                if (!cellData) {
                    return null;
                }

                return cellData;
            });
    }

    private _renderProductCell(product: AttributeValue): JSX.Element | null {
        switch (product.DataTypeValue) {
            case 0: // None
                // Return span here instead of null so the row still shows
                return <span />;
            case 1: // Currency
                return this._renderCurrencyCell(product);
            case 2: // DateTime
                return this._renderDateTimeCell(product);
            case 3: // Decimal
                return this._renderDecimalCell(product);
            case 4: // Integer
                return this._renderIntegerCell(product);
            case 5: // Text
                return this._renderTextCell(product);
            case 6: // TrueFalse
                return this._renderTrueFalseCell(product);
            case 40: // Video
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case 41: // Image
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
                return null;
        }
    }

    private _renderCurrencyCell(product: AttributeValue): JSX.Element | null {
        if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
            return (
                <span>
                    {product.CurrencyCode}
                    {product.CurrencyValue}
                </span>
            );
        }

        return null;
    }

    private _renderDateTimeCell(product: AttributeValue): JSX.Element | null {
        if (product.DateTimeOffsetValue !== null && product.DateTimeOffsetValue !== undefined && product.DateTimeOffsetValue.toUTCString) {
            return <span>{product.DateTimeOffsetValue.toUTCString()}</span>;
        }

        return null;
    }

    private _renderIntegerCell(product: AttributeValue): JSX.Element | null {
        if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
            return <span>{product.IntegerValue}</span>;
        }

        return null;
    }

    private _renderDecimalCell(product: AttributeValue): JSX.Element | null {
        if (product.FloatValue !== null && product.FloatValue !== undefined) {
            return <span>{product.FloatValue}</span>;
        }

        return null;
    }

    private _renderTextCell(product: AttributeValue): JSX.Element | null {
        if (product.TextValue !== null && product.TextValue !== undefined) {
            const values = product.TextValue.split('|');
            const classname = values.length > 1 ? 'lobas-attribute-text' : '';

            if (values.length > 1) {
                const data = values.map((text, index) => {
                    return <span key={index} className={`${classname}`} dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(text)} />;
                });

                return <div className='lobas-attribute-group'>{data}</div>;
            } else {
                return (
                    <span
                        className={`${this.props.config.className}__attribute-text`}
                        dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(product.TextValue)}
                    />
                );
            }
        }

        return null;
    }

    private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
        // TODO 20369885 setting to default value is needed to work around a platform bug where
        // config string value does not get its default value.
        if (product.BooleanValue) {
            return <span>{this.props.resources.trueValueText ? this.props.resources.trueValueText : 'Yes'}</span>;
        }
        return <span>{this.props.resources.falseValueText ? this.props.resources.falseValueText : 'No'}</span>;
    }
}

export default LobasProductAttribute;
